/**
 * Editable Knowledge Base display — port of
 * `fe-prototype/build/KnowledgeBase.jsx` against the live API.
 *
 * Two panels:
 *
 *   - "What it still needs" — `KbItem`s with `status='needs_you'`, the
 *     prototype's gaps surfaced as a flat list. Each gap renders inline
 *     with a `<input>` + Save button; on save the item flips to
 *     `confirmed` and moves out of the gaps panel into the main "What
 *     it knows" list. (No separate gaps concept on the backend — a gap
 *     is just an item with `status='needs_you'`.)
 *   - "What it knows" — the full `kb_groups`. Each row gets a pencil
 *     button that swaps the value for an inline `<input>`; Enter saves,
 *     Escape cancels. Hours items render through `HoursRow` instead
 *     (live 7-day schedule, plain-English editor).
 *
 * Optimistic updates: on save we hit `PATCH /kb/{slug}/items` and
 * replace the local state with the response payload. On non-2xx the
 * local state reverts and a toast is shown for ~3.2s. The 429 case
 * gets a distinct "rate limit" toast so the visitor knows to wait.
 */

import { useCallback, useState } from "react";

import { Icon } from "./Icon.js";
import { HoursRow, isHoursItem } from "./HoursRow.js";
import { KbEditError, patchKbItem, postKbGroup, postKbItem } from "./api.js";
import type {
  BusinessProfile,
  KbGroup,
  KbItem,
  KbItemStatus,
} from "./types.js";

interface KbDisplayProps {
  slug: string;
  profile: BusinessProfile;
  onProfileUpdate: (profile: BusinessProfile) => void;
}

/**
 * Toast state — shown for ~3.2s after a save error. `tone` lets the
 * pill colour match the severity (warning for 429, error for 500).
 */
interface Toast {
  tone: "warn" | "error";
  text: string;
}

/**
 * Status pill that mirrors the prototype's `.status-pill.confirmed` /
 * `.status-pill.review` styling. We keep "needs you" wording (rather
 * than the typed status `needs_you`) for the visible label.
 */
function StatusBadge({ status }: { status: KbItem["status"] }) {
  if (status === "confirmed") {
    return (
      <span className="status-pill confirmed">
        <Icon name="check" size={11} stroke={3} />
        confirmed
      </span>
    );
  }
  return <span className="status-pill review">needs you</span>;
}

export function KbDisplay({ slug, profile, onProfileUpdate }: KbDisplayProps) {
  const { name: businessName, kb_groups: kbGroups } = profile;
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((next: Toast) => {
    setToast(next);
    window.setTimeout(() => setToast((t) => (t === next ? null : t)), 3200);
  }, []);

  /**
   * Save an edit against the API. On success, replaces the parent
   * profile with the API response. On error, surfaces a toast keyed
   * on the HTTP status; the local state has not been mutated, so
   * "revert" is implicit.
   */
  const save = useCallback(
    async (
      groupName: string,
      itemKey: string,
      value: string,
      status: KbItemStatus,
    ) => {
      const editKey = `${groupName}::${itemKey}`;
      setSavingKey(editKey);
      try {
        const updated = await patchKbItem(slug, {
          group_name: groupName,
          item_key: itemKey,
          value,
          status,
        });
        onProfileUpdate(updated);
        setEditing(null);
      } catch (err) {
        if (err instanceof KbEditError && err.status === 429) {
          showToast({
            tone: "warn",
            text: "Too many edits — please try again later.",
          });
        } else {
          const detail =
            err instanceof Error ? err.message : "Save failed — try again.";
          showToast({ tone: "error", text: detail });
        }
      } finally {
        setSavingKey(null);
      }
    },
    [slug, onProfileUpdate, showToast],
  );

  /**
   * Create a brand-new item inside an existing group via POST /kb/{slug}/items.
   * Mirrors `save`'s error mapping but adds a 409 toast for the
   * "key already exists" case so the owner-operator gets a clear hint
   * rather than a generic save-failed.
   */
  const addItem = useCallback(
    async (groupName: string, key: string, value: string) => {
      setSavingKey(`add::${groupName}`);
      try {
        const updated = await postKbItem(slug, {
          group_name: groupName,
          item_key: key,
          value,
          status: "confirmed",
        });
        onProfileUpdate(updated);
        return true;
      } catch (err) {
        if (err instanceof KbEditError) {
          if (err.status === 409) {
            showToast({
              tone: "warn",
              text: `"${key}" already exists in this section.`,
            });
          } else if (err.status === 429) {
            showToast({
              tone: "warn",
              text: "Too many edits — please try again later.",
            });
          } else {
            showToast({ tone: "error", text: err.detail });
          }
        } else {
          showToast({ tone: "error", text: "Add failed — try again." });
        }
        return false;
      } finally {
        setSavingKey(null);
      }
    },
    [slug, onProfileUpdate, showToast],
  );

  /**
   * Create a brand-new section via POST /kb/{slug}/groups, seeded with
   * a single item. 409 means the section name collides with an
   * existing one; surface the distinct toast.
   */
  const addGroup = useCallback(
    async (name: string, itemKey: string, itemValue: string) => {
      setSavingKey("add::__group__");
      try {
        const updated = await postKbGroup(slug, {
          name,
          icon: "tag",
          items: [{ key: itemKey, value: itemValue, status: "confirmed" }],
        });
        onProfileUpdate(updated);
        return true;
      } catch (err) {
        if (err instanceof KbEditError) {
          if (err.status === 409) {
            showToast({
              tone: "warn",
              text: `A section named "${name}" already exists.`,
            });
          } else if (err.status === 429) {
            showToast({
              tone: "warn",
              text: "Too many edits — please try again later.",
            });
          } else {
            showToast({ tone: "error", text: err.detail });
          }
        } else {
          showToast({ tone: "error", text: "Add failed — try again." });
        }
        return false;
      } finally {
        setSavingKey(null);
      }
    },
    [slug, onProfileUpdate, showToast],
  );

  const gaps = kbGroups.flatMap((g) =>
    g.items
      .filter((it) => it.status === "needs_you")
      .map((it) => ({ group: g, item: it })),
  );

  return (
    <div>
      <div className="section-head">
        <h2>Knowledge base</h2>
        <p>
          Everything your offsider knows about {businessName}. Fix anything
          that's off, and fill in what we couldn't find.
        </p>
      </div>

      {toast && (
        <div
          role="alert"
          data-testid="kb-edit-toast"
          className={`status-pill ${toast.tone === "warn" ? "review" : "review"}`}
          style={{
            display: "block",
            marginBottom: 12,
            padding: "8px 12px",
            background:
              toast.tone === "warn"
                ? "var(--warn-bg, #fff7ed)"
                : "var(--err-bg, #fff1f2)",
            borderColor:
              toast.tone === "warn"
                ? "var(--warn, #c2410c)"
                : "var(--err, #be123c)",
            color:
              toast.tone === "warn"
                ? "var(--warn, #c2410c)"
                : "var(--err, #be123c)",
          }}
        >
          {toast.text}
        </div>
      )}

      {gaps.length > 0 && (
        <div className="panel" style={{ marginBottom: 18 }}>
          <div className="panel-h">
            <Icon name="circle-help" size={18} color="var(--warn)" />
            <h3>What it still needs</h3>
            <span className="count">{gaps.length} to add</span>
          </div>
          {gaps.map(({ group, item }) => (
            <GapField
              key={`${group.name}::${item.key}`}
              group={group}
              item={item}
              busy={savingKey === `${group.name}::${item.key}`}
              onSave={(value) => save(group.name, item.key, value, "confirmed")}
            />
          ))}
        </div>
      )}

      <div className="panel">
        <div className="panel-h">
          <Icon name="book-open" size={18} color="var(--ink-2)" />
          <h3>What it knows</h3>
        </div>
        {kbGroups.length === 0 ? (
          <div
            style={{
              padding: 20,
              fontSize: 14,
              color: "var(--ink-3)",
              textAlign: "center",
            }}
          >
            No facts extracted yet. Try a richer source — your website rather
            than a bare business name.
          </div>
        ) : (
          kbGroups.map((g) => (
            <div key={g.name} className="kb-group">
              <div className="kb-gh">
                <span className="ico">
                  <Icon name={g.icon} size={16} />
                </span>
                <h4>{g.name}</h4>
              </div>
              {g.items.map((it, ii) => {
                const editKey = `${g.name}::${it.key}`;
                if (isHoursItem(g.name, it.key)) {
                  return (
                    <HoursRow
                      key={`${it.key}-${ii}`}
                      item={it}
                      saving={savingKey === editKey}
                      onSave={(value) =>
                        save(g.name, it.key, value, "confirmed")
                      }
                    />
                  );
                }
                const isEditing = editing === editKey;
                const saving = savingKey === editKey;
                return (
                  <div
                    key={`${it.key}-${ii}`}
                    className="kb-item"
                    data-testid={`kb-row-${it.key}`}
                  >
                    <div className="kb-k">{it.key}</div>
                    <div className="kb-v">
                      {isEditing ? (
                        <input
                          className="kb-edit"
                          autoFocus
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              void save(g.name, it.key, draft, "confirmed");
                            }
                            if (e.key === "Escape") {
                              setEditing(null);
                            }
                          }}
                          aria-label={`Edit ${it.key}`}
                          data-testid={`kb-edit-input-${it.key}`}
                        />
                      ) : (
                        <>
                          {it.value}
                          <div className="src">
                            <StatusBadge status={it.status} />
                            <span style={{ marginLeft: 8 }}>
                              from {it.source}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="kb-actions">
                      {isEditing ? (
                        <button
                          type="button"
                          className="icon-btn ok"
                          onClick={() =>
                            void save(g.name, it.key, draft, "confirmed")
                          }
                          disabled={saving}
                          aria-label="Save"
                          data-testid={`kb-save-${it.key}`}
                        >
                          <Icon name="check" size={16} stroke={2.6} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => {
                            setEditing(editKey);
                            setDraft(it.value);
                          }}
                          aria-label={`Edit ${it.key}`}
                          data-testid={`kb-pencil-${it.key}`}
                        >
                          <Icon name="pencil" size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <AddItemRow
                groupName={g.name}
                busy={savingKey === `add::${g.name}`}
                onAdd={(key, value) => addItem(g.name, key, value)}
              />
            </div>
          ))
        )}
        {kbGroups.length > 0 && (
          <AddGroupRow
            busy={savingKey === "add::__group__"}
            onAdd={(name, itemKey, itemValue) =>
              addGroup(name, itemKey, itemValue)
            }
          />
        )}
      </div>
    </div>
  );
}

interface AddItemRowProps {
  groupName: string;
  busy: boolean;
  onAdd: (key: string, value: string) => Promise<boolean>;
}

/**
 * Inline "+ Add item" row at the bottom of each KB group. Collapsed
 * by default to a single button; expands into a (key, value) pair when
 * clicked. Enter commits, Escape cancels and re-collapses. Clears its
 * inputs on a successful add so the owner-operator can append several
 * items in a row without re-clicking the button.
 */
function AddItemRow({ groupName, busy, onAdd }: AddItemRowProps) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const commit = async () => {
    const k = key.trim();
    const v = value.trim();
    if (!k || !v) return;
    const ok = await onAdd(k, v);
    if (ok) {
      setKey("");
      setValue("");
      setOpen(false);
    }
  };
  const cancel = () => {
    setKey("");
    setValue("");
    setOpen(false);
  };
  if (!open) {
    return (
      <div
        className="kb-add-row"
        style={{
          padding: "10px 18px 10px 58px",
          borderTop: "1px dashed var(--rule)",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Add item to ${groupName}`}
          data-testid={`kb-add-item-btn-${groupName}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            fontSize: 13,
            fontFamily: "var(--font-ui)",
            color: "var(--ink-3)",
            background: "transparent",
            border: "1px dashed var(--rule-2)",
            borderRadius: 9,
            cursor: "pointer",
            transition: "all 0.15s var(--ease)",
          }}
          onMouseEnter={(e) => {
            const t = e.currentTarget;
            t.style.color = "var(--ink-1)";
            t.style.borderColor = "var(--ink-3)";
            t.style.background = "var(--bg-1)";
          }}
          onMouseLeave={(e) => {
            const t = e.currentTarget;
            t.style.color = "var(--ink-3)";
            t.style.borderColor = "var(--rule-2)";
            t.style.background = "transparent";
          }}
        >
          <Icon name="plus" size={14} />
          Add item
        </button>
      </div>
    );
  }
  return (
    <div
      className="kb-item kb-add-open"
      data-testid={`kb-add-item-row-${groupName}`}
    >
      <div className="kb-k">
        <input
          className="kb-edit"
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void commit();
            if (e.key === "Escape") cancel();
          }}
          placeholder="Field name (e.g. Languages spoken)"
          aria-label={`New item key for ${groupName}`}
          data-testid={`kb-add-item-key-${groupName}`}
        />
      </div>
      <div className="kb-v">
        <input
          className="kb-edit"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void commit();
            if (e.key === "Escape") cancel();
          }}
          placeholder="Value (e.g. English, Mandarin)"
          aria-label={`New item value for ${groupName}`}
          data-testid={`kb-add-item-value-${groupName}`}
        />
      </div>
      <div className="kb-actions">
        <button
          type="button"
          className="icon-btn ok"
          onClick={() => void commit()}
          disabled={busy || !key.trim() || !value.trim()}
          aria-label="Save new item"
          data-testid={`kb-add-item-save-${groupName}`}
        >
          <Icon name="check" size={16} stroke={2.6} />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={cancel}
          aria-label="Cancel"
          data-testid={`kb-add-item-cancel-${groupName}`}
        >
          <Icon name="x" size={15} />
        </button>
      </div>
    </div>
  );
}

interface AddGroupRowProps {
  busy: boolean;
  onAdd: (name: string, itemKey: string, itemValue: string) => Promise<boolean>;
}

/**
 * "+ Add section" row at the bottom of the KB panel. Same collapsed/
 * expanded pattern as `AddItemRow`. Backend requires at least one
 * initial item, so the form takes section name + one (key, value) pair
 * up-front. Subsequent items can be added via the in-group + Add item
 * affordance once the section exists.
 */
function AddGroupRow({ busy, onAdd }: AddGroupRowProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [itemKey, setItemKey] = useState("");
  const [itemValue, setItemValue] = useState("");
  const reset = () => {
    setName("");
    setItemKey("");
    setItemValue("");
    setOpen(false);
  };
  const commit = async () => {
    const n = name.trim();
    const k = itemKey.trim();
    const v = itemValue.trim();
    if (!n || !k || !v) return;
    const ok = await onAdd(n, k, v);
    if (ok) reset();
  };
  if (!open) {
    return (
      <div
        className="kb-add-section-row"
        style={{
          padding: "14px 18px",
          borderTop: "1px solid var(--rule)",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Add a new section"
          data-testid="kb-add-section-btn"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            fontSize: 13,
            fontFamily: "var(--font-ui)",
            fontWeight: 500,
            color: "var(--ink-2)",
            background: "var(--paper)",
            border: "1px dashed var(--rule-2)",
            borderRadius: 10,
            cursor: "pointer",
            transition: "all 0.15s var(--ease)",
          }}
          onMouseEnter={(e) => {
            const t = e.currentTarget;
            t.style.color = "var(--ink-1)";
            t.style.borderColor = "var(--ink-3)";
            t.style.background = "var(--bg-1)";
          }}
          onMouseLeave={(e) => {
            const t = e.currentTarget;
            t.style.color = "var(--ink-2)";
            t.style.borderColor = "var(--rule-2)";
            t.style.background = "var(--paper)";
          }}
        >
          <Icon name="plus" size={14} />
          Add section
        </button>
      </div>
    );
  }
  return (
    <div
      className="kb-group kb-add-group-open"
      data-testid="kb-add-section-row"
      style={{ marginTop: 8 }}
    >
      <div className="kb-gh">
        <span className="ico">
          <Icon name="tag" size={16} />
        </span>
        <input
          className="kb-edit"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") reset();
          }}
          placeholder="Section name (e.g. Languages)"
          aria-label="New section name"
          data-testid="kb-add-section-name"
          style={{ flex: 1 }}
        />
      </div>
      <div className="kb-item">
        <div className="kb-k">
          <input
            className="kb-edit"
            value={itemKey}
            onChange={(e) => setItemKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commit();
              if (e.key === "Escape") reset();
            }}
            placeholder="First field name"
            aria-label="First item key"
            data-testid="kb-add-section-item-key"
          />
        </div>
        <div className="kb-v">
          <input
            className="kb-edit"
            value={itemValue}
            onChange={(e) => setItemValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commit();
              if (e.key === "Escape") reset();
            }}
            placeholder="Value"
            aria-label="First item value"
            data-testid="kb-add-section-item-value"
          />
        </div>
        <div className="kb-actions">
          <button
            type="button"
            className="icon-btn ok"
            onClick={() => void commit()}
            disabled={
              busy || !name.trim() || !itemKey.trim() || !itemValue.trim()
            }
            aria-label="Create section"
            data-testid="kb-add-section-save"
          >
            <Icon name="check" size={16} stroke={2.6} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={reset}
            aria-label="Cancel"
            data-testid="kb-add-section-cancel"
          >
            <Icon name="x" size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface GapFieldProps {
  group: KbGroup;
  item: KbItem;
  busy: boolean;
  onSave: (value: string) => Promise<void> | void;
}

/**
 * Inline gap-filling row. Port of the prototype's `GapField`. The
 * input mounts empty; Enter or the Save button commit the draft.
 * Whitespace-only inputs are ignored so a stray Enter doesn't blank
 * out the gap.
 */
function GapField({ group, item, busy, onSave }: GapFieldProps) {
  const [val, setVal] = useState("");
  const commit = () => {
    const trimmed = val.trim();
    if (!trimmed) return;
    void onSave(trimmed);
  };
  return (
    <div className="gap-item" data-testid={`kb-gap-${item.key}`}>
      <div className="gap-k">
        <span className="status-pill review">needs you</span>
        {item.key}
      </div>
      <div className="gap-hint">
        From {group.name}. We couldn't find this on public sources.
      </div>
      <div className="gap-field">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
          aria-label={`Fill in ${item.key}`}
          data-testid={`kb-gap-input-${item.key}`}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={commit}
          disabled={busy || !val.trim()}
          data-testid={`kb-gap-save-${item.key}`}
        >
          <Icon name="check" size={14} />
          Save
        </button>
      </div>
    </div>
  );
}
